import { observable, makeObservable } from 'mobx';

const LOCAL_STORAGE_KEY = '936DA01F9ABD4d9d80C702AF85C822A8';

const ROLE_CLAIM_KEYS = [
  'role',
  'roles',
  'Role',
  'Roles',
  'ApplicationRole',
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
  'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/role',
];

const CLAIM_KEYS = {
  userId: ['UserId', 'userId', 'nameid', 'sub', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'],
  userGuid: ['UserGuid', 'userGuid'],
  email: [
    'Email',
    'email',
    'upn',
    'preferred_username',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress',
  ],
  userName: [
    'UserName',
    'userName',
    'unique_name',
    'name',
    'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name',
  ],
  firstName: ['FirstName', 'firstName', 'given_name'],
  lastName: ['LastName', 'lastName', 'family_name'],
  applicationId: ['ApplicationGuid', 'ApplicationId', 'applicationId'],
};

/*eslint no-use-before-define: 0*/
class LocalStorageStore {
  loading;

  defaultModule = null;

  constructor() {
    makeObservable(this, {
      defaultModule: observable,
    });
    this.loading = false;
  }

  getStorageSlot = () => {
    try {
      return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || 'null');
    } catch (error) {
      return null;
    }
  };

  getClaimValue = (payload, keys) => {
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      if (payload?.[key] !== undefined && payload?.[key] !== null && payload?.[key] !== '') {
        return payload[key];
      }
    }
    return null;
  };

  getRolesFromPayload = (payload) => {
    const roles = ROLE_CLAIM_KEYS.reduce((allRoles, key) => {
      const rawValue = payload?.[key];
      if (!rawValue) {
        return allRoles;
      }
      const values = Array.isArray(rawValue) ? rawValue : [rawValue];
      return allRoles.concat(values);
    }, []);

    return roles.filter(Boolean).filter((value, index, source) => source.indexOf(value) === index);
  };

  getProfile = () => {
    const slot = this.getStorageSlot();
    return slot?.p || null;
  };

  saveProfile = (profile) => {
    const slot = this.getStorageSlot() || { t: '' };
    slot.p = profile;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(slot));
  };

  buildUser = (payload) => {
    const profile = this.getProfile() || {};
    const fullName = profile.fullName || [profile.firstName, profile.lastName].filter(Boolean).join(' ');
    const firstName =
      profile.firstName || this.getClaimValue(payload, CLAIM_KEYS.firstName) || fullName.split(' ')[0] || '';
    const lastName =
      profile.lastName ||
      this.getClaimValue(payload, CLAIM_KEYS.lastName) ||
      fullName.split(' ').slice(1).join(' ') ||
      '';
    const email = profile.email || this.getClaimValue(payload, CLAIM_KEYS.email) || '';
    const roles = this.getRolesFromPayload(payload);

    return {
      UserId: profile.userFriendlyId || this.getClaimValue(payload, CLAIM_KEYS.userId) || '',
      UserGuid: profile.id || this.getClaimValue(payload, CLAIM_KEYS.userGuid) || '',
      UserName: this.getClaimValue(payload, CLAIM_KEYS.userName) || email,
      Email: email,
      FirstName: firstName,
      LastName: lastName,
      Roles: roles,
      ApplicationGuid: this.getClaimValue(payload, CLAIM_KEYS.applicationId) || '',
    };
  };

  getFullname = () => {
    const user = this.getToken().user || {};
    return [user.FirstName, user.LastName].filter(Boolean).join(' ').trim() || user.Email || user.UserName || '';
  };

  firstName = () => {
    return this.getToken().user?.FirstName || '';
  };

  lastName = () => {
    return this.getToken().user?.LastName || '';
  };

  getRoles = () => {
    return this.getToken().user?.Roles || [];
  };

  getUserId = () => {
    return this.getToken().user?.UserId || '';
  };

  getUserGuid = () => {
    return this.getToken().user?.UserGuid || '';
  };

  getEmail = () => {
    return this.getToken().user?.Email || '';
  };

  getUserName = () => {
    return this.getToken().user?.UserName || '';
  };

  getExpiryTime = () => {
    return this.getToken()?.exp || 0;
  };

  saveToken = (jwt) => {
    const slot = {
      t: jwt.access_token || jwt.token || jwt.accessToken || '',
      r: jwt.refresh_token || jwt.refreshToken || '',
      p: jwt.profile || null,
      m: jwt.metadata || null,
      rm: jwt.rememberMe === true,
    };

    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(slot));
  };

  // Updates only t and p — r, m, rm are preserved from the existing slot.
  updateTokenAndProfile = (token, profile) => {
    const slot = this.getStorageSlot() || {};
    slot.t = token;
    slot.p = profile;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(slot));
  };

  getRememberMe = () => {
    return this.getStorageSlot()?.rm !== false;
  };

  removeToken = () => {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  };

  isTokenExists = () => {
    return localStorage.getItem(LOCAL_STORAGE_KEY) !== null;
  };

  getToken = () => {
    return this.parseJwt(this.getStorageSlot());
  };

  get getTokenE() {
    const token = this.getStorageSlot() || { t: '' };
    return token.t || '';
  }

  parseJwt = (token) => {
    if (!token?.t) {
      return { user: {}, exp: 0, roles: [] };
    }

    try {
      const base64Url = token.t.split('.')[1];
      if (!base64Url) {
        return { user: {}, exp: 0, roles: [] };
      }

      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));
      const user = this.buildUser(payload);

      return {
        ...payload,
        user,
        roles: user.Roles,
        metadata: token.m || null,
      };
    } catch (error) {
      return { user: {}, exp: 0, roles: [] };
    }
  };
}

const localStorageStore = new LocalStorageStore();

export default localStorageStore;
